/**
 * Role definitions, display names, and hierarchy.
 */

import type { Role } from "./types";

export const ROLE_DISPLAY_NAMES: Record<Role, string> = {
  admin: "Admin",
  supervising_pharmacist: "Supervising Pharmacist",
  pharmacist: "Pharmacist",
  pharmacy_intern: "Pharmacy Intern",
  technician: "Technician",
  auditor: "Auditor",
};

/**
 * Roles ordered by privilege level (highest to lowest).
 * Used to determine approver eligibility.
 */
export const ROLE_HIERARCHY: Role[] = [
  "admin",
  "supervising_pharmacist",
  "pharmacist",
  "pharmacy_intern",
  "technician",
  "auditor",
];

/**
 * Roles that can approve actions on behalf of others.
 */
export const APPROVER_ROLES: Role[] = [
  "admin",
  "supervising_pharmacist",
  "pharmacist",
];

export function getRoleDisplayName(role: Role): string {
  return ROLE_DISPLAY_NAMES[role] ?? role;
}

export function isApproverRole(role: Role): boolean {
  return APPROVER_ROLES.includes(role);
}

/** Access level tag for permission summary items. */
export type AccessTag = "full" | "limited" | "approval" | "view" | "none";

export interface PermissionSummaryItem {
  label: string;
  access: AccessTag;
}

/**
 * Returns a high-level permission summary for a role.
 * Used in the user creation form to show admins what access a role grants.
 */
export function getRolePermissionSummary(role: Role): PermissionSummaryItem[] {
  switch (role) {
    case "admin":
    case "supervising_pharmacist":
      return [
        { label: "User management", access: "full" },
        { label: "Inventory & products", access: "full" },
        { label: "Dispenses & receipts", access: "full" },
        { label: "Stock adjustments", access: "full" },
        { label: "Reports & audit log", access: "full" },
        { label: "Pharmacy settings", access: "full" },
        { label: "Can authorize others", access: "full" },
      ];
    case "pharmacist":
      return [
        { label: "User management", access: "none" },
        { label: "Inventory & products", access: "full" },
        { label: "Dispenses & receipts", access: "full" },
        { label: "Stock adjustments", access: "full" },
        { label: "Reports & audit log", access: "full" },
        { label: "Pharmacy settings", access: "none" },
        { label: "Can authorize others", access: "full" },
      ];
    case "pharmacy_intern":
      return [
        { label: "User management", access: "none" },
        { label: "Inventory & products", access: "full" },
        { label: "Dispenses & receipts", access: "limited" },
        { label: "Stock adjustments", access: "approval" },
        { label: "Reports & audit log", access: "full" },
        { label: "Pharmacy settings", access: "none" },
        { label: "Can authorize others", access: "none" },
      ];
    case "technician":
      return [
        { label: "User management", access: "none" },
        { label: "Inventory & products", access: "limited" },
        { label: "Dispenses & receipts", access: "limited" },
        { label: "Stock adjustments", access: "approval" },
        { label: "Reports", access: "view" },
        { label: "Pharmacy settings", access: "none" },
      ];
    case "auditor":
      return [
        { label: "User management", access: "none" },
        { label: "Inventory & products", access: "view" },
        { label: "Dispenses & receipts", access: "view" },
        { label: "Stock adjustments", access: "none" },
        { label: "Reports & audit log", access: "full" },
        { label: "Pharmacy settings", access: "none" },
        { label: "Can authorize others", access: "none" },
      ];
  }
}
